import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../api/remembrApi';
import { logger } from '../../utils/logger';
import type { RootState } from '../../store/store';
import type { 
  InventoryItem,
  UserStatisticsResponse,
  NetWorthStats,
  InventorySummaryStats,
  AchievementStats,
  ActivityStats,
} from '../../api/types';

interface DashboardState {
  // Enhanced statistics from new API
  netWorth: NetWorthStats | null;
  inventorySummary: InventorySummaryStats | null;
  achievements: AchievementStats | null;
  activity: ActivityStats | null;
  
  // Additional data
  recentItems: InventoryItem[];
  lastUpdated: string | null;
  cached: boolean;
  calculationTime: number;
  
  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  netWorth: null,
  inventorySummary: null,
  achievements: null,
  activity: null,
  recentItems: [],
  lastUpdated: null,
  cached: false,
  calculationTime: 0,
  isLoading: false,
  isRefreshing: false,
  error: null,
};

// Enhanced async thunk using new statistics API
export const loadEnhancedDashboardData = createAsyncThunk(
  'dashboard/loadEnhancedData',
  async (refresh: boolean = false, { rejectWithValue }) => {
    try {
      console.log('🎯 Loading enhanced dashboard data...', { refresh });
      
      // Load enhanced statistics
      const statisticsResponse = await api.getUserStatistics(refresh);
      console.log('✅ Enhanced statistics response:', statisticsResponse);

      // Load some recent inventory items for display
      let recentItems: InventoryItem[] = [];
      try {
        const inventoryResponse = await api.getInventory(0, 5);
        recentItems = inventoryResponse.items;
        console.log('✅ Recent items loaded:', recentItems.length);
      } catch (inventoryError) {
        logger.warn('⚠️ Could not load recent items:', inventoryError);
        // Continue without recent items
      }

      return {
        ...statisticsResponse,
        recentItems,
      };
    } catch (error: any) {
      logger.error('Failed to load enhanced dashboard data', error);
      return rejectWithValue(error.response?.data?.detail || 'Failed to load dashboard data');
    }
  }
);

// Legacy thunk for backward compatibility
export const loadDashboardData = createAsyncThunk(
  'dashboard/loadData',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🎯 Loading dashboard data (legacy)...');
      
      // Load enhanced statistics
      const statisticsResponse = await api.getUserStatistics(false);
      console.log('✅ Enhanced statistics response:', statisticsResponse);

      // Load some recent inventory items for display
      let recentItems: InventoryItem[] = [];
      try {
        const inventoryResponse = await api.getInventory(0, 5);
        recentItems = inventoryResponse.items;
        console.log('✅ Recent items loaded:', recentItems.length);
      } catch (inventoryError) {
        console.warn('⚠️ Could not load recent items:', inventoryError);
        // Continue without recent items
      }

      return {
        ...statisticsResponse,
        recentItems,
      };
    } catch (error: any) {
      logger.error('Failed to load dashboard data', error);
      return rejectWithValue(error.response?.data?.detail || 'Failed to load dashboard data');
    }
  }
);

// Refresh data with cache bypass
export const refreshDashboardData = createAsyncThunk(
  'dashboard/refreshData',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Refreshing dashboard data...');
      
      // Load enhanced statistics with refresh
      const statisticsResponse = await api.getUserStatistics(true);
      console.log('✅ Enhanced statistics refreshed:', statisticsResponse);

      // Load some recent inventory items for display
      let recentItems: InventoryItem[] = [];
      try {
        const inventoryResponse = await api.getInventory(0, 5);
        recentItems = inventoryResponse.items;
        console.log('✅ Recent items loaded:', recentItems.length);
      } catch (inventoryError) {
        console.warn('⚠️ Could not load recent items:', inventoryError);
        // Continue without recent items
      }

      return {
        ...statisticsResponse,
        recentItems,
      };
    } catch (error: any) {
      logger.error('Failed to refresh dashboard data', error);
      return rejectWithValue(error.response?.data?.detail || 'Failed to refresh dashboard data');
    }
  }
);

// Slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Enhanced dashboard data loading
      .addCase(loadEnhancedDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadEnhancedDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        
        const { recentItems, ...statistics } = action.payload;
        
        state.netWorth = statistics.net_worth;
        state.inventorySummary = statistics.inventory_summary;
        state.achievements = statistics.achievements;
        state.activity = statistics.activity;
        state.recentItems = recentItems;
        state.lastUpdated = statistics.last_updated;
        state.cached = statistics.cached;
        state.calculationTime = statistics.calculation_time_ms;
        state.error = null;
      })
      .addCase(loadEnhancedDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = action.payload as string;
      })
      
      // Legacy dashboard data loading
      .addCase(loadDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        
        const { recentItems, ...statistics } = action.payload;
        
        state.netWorth = statistics.net_worth;
        state.inventorySummary = statistics.inventory_summary;
        state.achievements = statistics.achievements;
        state.activity = statistics.activity;
        state.recentItems = recentItems;
        state.lastUpdated = statistics.last_updated;
        state.cached = statistics.cached;
        state.calculationTime = statistics.calculation_time_ms;
        state.error = null;
      })
      .addCase(loadDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Refresh dashboard data
      .addCase(refreshDashboardData.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        
        const { recentItems, ...statistics } = action.payload;
        
        state.netWorth = statistics.net_worth;
        state.inventorySummary = statistics.inventory_summary;
        state.achievements = statistics.achievements;
        state.activity = statistics.activity;
        state.recentItems = recentItems;
        state.lastUpdated = statistics.last_updated;
        state.cached = statistics.cached;
        state.calculationTime = statistics.calculation_time_ms;
        state.error = null;
      })
      .addCase(refreshDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setRefreshing } = dashboardSlice.actions;

// Enhanced selectors
export const selectDashboardState = (state: RootState) => state.dashboard;
export const selectNetWorthStats = (state: RootState) => state.dashboard.netWorth;
export const selectInventorySummaryStats = (state: RootState) => state.dashboard.inventorySummary;
export const selectAchievementStats = (state: RootState) => state.dashboard.achievements;
export const selectActivityStats = (state: RootState) => state.dashboard.activity;
export const selectRecentItems = (state: RootState) => state.dashboard.recentItems;
export const selectDashboardLoading = (state: RootState) => state.dashboard.isLoading;
export const selectDashboardRefreshing = (state: RootState) => state.dashboard.isRefreshing;
export const selectDashboardError = (state: RootState) => state.dashboard.error;
export const selectLastUpdated = (state: RootState) => state.dashboard.lastUpdated;
export const selectIsCached = (state: RootState) => state.dashboard.cached;
export const selectCalculationTime = (state: RootState) => state.dashboard.calculationTime;

// Legacy selectors for backward compatibility
export const selectDashboardStats = (state: RootState) => {
  const { inventorySummary } = state.dashboard;
  if (!inventorySummary) {
    return {
      totalFiles: 0,
      totalItems: 0,
      categories: {},
      recentUploads: 0,
    };
  }
  
  return {
    totalFiles: inventorySummary.total_files,
    totalItems: inventorySummary.total_items,
    categories: {}, // Would need to be calculated separately if needed
    recentUploads: inventorySummary.items_this_month,
  };
};

export default dashboardSlice.reducer; 